import {
  AgentAccessRequest,
  AgentAccessRequestStatus,
  AgentDataDetailed,
  AgentListResponse,
  AgentDataReduced,
  AgentEndpointData,
  AgentAccessReviewStatus,
  AgentAccessUpdateResult,
  Massp4aiUseCase,
} from '../../api/agentgarden.objects';

import { AgentGardenApi } from '../../api/agentgarden.api';
import {
  SimulateRoleRequest,
  SimulateRoleResponse,
} from '../../api/dev-mode.types';

import { DiscoveryApi } from '@backstage/core-plugin-api';

import { RequestAgentAccessDialogFormData } from '../../components/Agent Garden/RequestAccess/RequestAgentAccessDialog/RequestAgentAccessDialogFormData';
import { isDatabricksAgent } from '../../utils/AgentHelpers';
import { ContactData } from '../../api/global.objects';

/**
 * Enum for Agent Garden Endpoints
 *
 * @remarks
 * - agents: Endpoint to get all agents
 * - accessRequests: Endpoint to get all or update access requests
 * - providerAccessReview: Endpoint to update agent ownership
 */
export const AgentGardenEndpoints = {
  agents: '/agents',
  accessRequests: '/access-requests',
  providerAccessReview: '/provider-access-review',
  masspUseCaseInfo: '/massp4ai/ai-use-case/get-by-id',
  availability: '/availability',
};

const MASSP4AI_USE_CASE_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const MASSP4AI_USE_CASE_FIELDS = [
  'name',
  'shortDescription',
  'expectedCompanyBenefit',
  'expectedCustomerBenefit',
  'businessNeed',
] as const;

/**
 * Reduces the MASSP4AI response to the fields shown in Agent Garden.
 *
 * @param id The MASSP4AI use case ID (UUID)
 * @param body The raw response from the MASSP4AI API
 * @returns A reduced object containing only the fields shown in Agent Garden
 */
function toMassp4aiUseCase(
  id: string,
  body: Record<string, unknown>,
): Massp4aiUseCase {
  const useCase: Massp4aiUseCase = {
    id: typeof body.id === 'string' ? body.id : id,
  };
  for (const field of MASSP4AI_USE_CASE_FIELDS) {
    const value = body[field];
    if (typeof value === 'string') {
      useCase[field] = value;
    }
  }

  const scanResults: unknown[] = Array.isArray(body.scanResults)
    ? body.scanResults
    : [];
  const mitigationMeasures = scanResults.find(
    (scanResult): scanResult is { domain: string; status?: unknown } =>
      typeof scanResult === 'object' &&
      scanResult !== null &&
      'domain' in scanResult &&
      scanResult.domain === 'MITIGATION_MEASURES',
  );
  if (typeof mitigationMeasures?.status === 'string') {
    useCase.mitigationMeasuresScanStatus = mitigationMeasures.status;
  }
  return useCase;
}

/**
 * Service to interact with the Agent Garden backend API
 */
export class AgentGardenService implements AgentGardenApi {
  private readonly discoveryApi: DiscoveryApi;

  constructor(discoveryApi: DiscoveryApi) {
    this.discoveryApi = discoveryApi;
  }

  /** @inheritdoc */
  async getAgents(limit?: number): Promise<AgentListResponse> {
    const agents: AgentDataReduced[] = [];
    let nextFirstItemKey: string | undefined = undefined;
    let isDevModeAuthorized: boolean | undefined = undefined;
    let canCreateUniversalAgents: boolean | undefined = undefined;

    do {
      const page = await this.getAgentsPage(limit, nextFirstItemKey);

      agents.push(...page.agents);
      nextFirstItemKey = page.lastItemKey;

      if (page.isDevModeAuthorized !== undefined) {
        isDevModeAuthorized = page.isDevModeAuthorized;
      }

      if (page.canCreateUniversalAgents !== undefined) {
        canCreateUniversalAgents = page.canCreateUniversalAgents;
      }
    } while (nextFirstItemKey);

    return {
      agents,
      canCreateUniversalAgents,
      isDevModeAuthorized,
    };
  }

  /** @inheritdoc */
  async getAgent(id: string): Promise<AgentDataDetailed> {
    const contentType = 'application/json';
    const proxyUrl = await this.discoveryApi.getBaseUrl('aiecosystem');

    const response = await fetch(
      `${proxyUrl}${AgentGardenEndpoints.agents}/${id}`,
      {
        method: 'GET',
        headers: {
          Accept: `${contentType}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Error - Statuscode ${response.status} - ${response.statusText} - Failed to fetch agent.`,
      );
    }

    const data = await response.json();

    const agent: AgentDataDetailed = {
      ...data,
    };

    return agent;
  }

  /** @inheritdoc */
  async getAgentAccessRequests(agentId: string): Promise<AgentAccessRequest[]> {
    const contentType = 'application/json';
    const proxyUrl = await this.discoveryApi.getBaseUrl('aiecosystem');

    const response = await fetch(
      `${proxyUrl}${AgentGardenEndpoints.agents}/${agentId}${AgentGardenEndpoints.accessRequests}`,
      {
        method: 'GET',
        headers: {
          Accept: `${contentType}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Error - Statuscode ${response.status} - ${response.statusText} - Failed to fetch access requests.`,
      );
    }

    const data = await response.json();

    return data.requests ?? [];
  }

  /** @inheritdoc */
  async getAgentNameAvailability(
    name: string,
    excludeAgentId?: string,
  ): Promise<{ available: boolean }> {
    const contentType = 'application/json';
    const proxyUrl = await this.discoveryApi.getBaseUrl('aiecosystem');

    const queryParams = new URLSearchParams();
    queryParams.append('name', name);

    if (excludeAgentId) {
      queryParams.append('exclude_agent_id', excludeAgentId);
    }

    const response = await fetch(
      `${proxyUrl}${AgentGardenEndpoints.agents}${AgentGardenEndpoints.availability}?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Accept: `${contentType}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Error - Statuscode ${response.status} - ${response.statusText} - Failed to check agent name availability.`,
      );
    }

    const data = await response.json();
    return { available: data.available };
  }

  /** @inheritdoc */
  async getMassp4aiUseCase(useCaseId: string): Promise<Massp4aiUseCase | null> {
    if (!MASSP4AI_USE_CASE_ID_PATTERN.test(useCaseId)) {
      throw new Error('Error. The ID is invalid or incorrectly formatted.');
    }

    const baseUrl = await this.discoveryApi.getBaseUrl('auth-gateway');
    const query = new URLSearchParams({ id: useCaseId });

    const response = await fetch(
      `${baseUrl}${AgentGardenEndpoints.masspUseCaseInfo}?${query.toString()}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      },
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(
        `Error - Statuscode ${response.status} - ${response.statusText} - Failed to fetch MASSP4AI use case.`,
      );
    }

    return toMassp4aiUseCase(
      useCaseId,
      (await response.json()) as Record<string, unknown>,
    );
  }

  /** @inheritdoc */
  async createAccessRequest(
    agentId: string,
    requestData: RequestAgentAccessDialogFormData,
  ): Promise<{ requestId: string; status: string; message: string }> {
    const contentType = 'application/json';
    const proxyUrl = await this.discoveryApi.getBaseUrl('aiecosystem');

    const response = await fetch(
      `${proxyUrl}${AgentGardenEndpoints.agents}/${agentId}${AgentGardenEndpoints.accessRequests}`,
      {
        method: 'POST',
        headers: {
          Accept: `${contentType}`,
          'Content-Type': `${contentType}`,
        },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      throw new Error(
        `Error - Statuscode ${response.status} - ${response.statusText} - Failed to create access request.`,
      );
    }

    return await response.json();
  }

  /** @inheritdoc */
  async updateAccessRequest(
    agentId: string,
    updateData: {
      requestIds: string[];
      decision: AgentAccessRequestStatus;
      decisionNotes: string;
    },
  ): Promise<{
    total: number;
    updatedCount: number;
    failedCount: number;
    results: AgentAccessUpdateResult[];
  }> {
    const contentType = 'application/json';
    const proxyUrl = await this.discoveryApi.getBaseUrl('aiecosystem');

    const response = await fetch(
      `${proxyUrl}${AgentGardenEndpoints.agents}/${agentId}${AgentGardenEndpoints.accessRequests}`,
      {
        method: 'PUT',
        headers: {
          Accept: `${contentType}`,
          'Content-Type': `${contentType}`,
        },
        body: JSON.stringify(updateData),
      },
    );

    if (!response.ok) {
      throw new Error(
        `Error - Statuscode ${response.status} - ${response.statusText} - Failed to update access request(s).`,
      );
    }

    return await response.json();
  }

  /** @inheritdoc */
  async unsubscribeFromAgent(
    agentId: string,
    requestId: string,
    updateData: {
      decision: AgentAccessRequestStatus;
      decisionNotes: string;
    },
  ): Promise<{
    requestId: string;
    status: string;
    decidedAtUTC: string;
    message: string;
  }> {
    const contentType = 'application/json';
    const proxyUrl = await this.discoveryApi.getBaseUrl('aiecosystem');

    const response = await fetch(
      `${proxyUrl}${AgentGardenEndpoints.agents}/${agentId}${AgentGardenEndpoints.accessRequests}/${requestId}`,
      {
        method: 'PUT',
        headers: {
          Accept: `${contentType}`,
          'Content-Type': `${contentType}`,
        },
        body: JSON.stringify(updateData),
      },
    );

    if (!response.ok) {
      throw new Error(
        `Error - Statuscode ${response.status} - ${response.statusText} - Failed to update access request.`,
      );
    }

    return await response.json();
  }

  /** @inheritdoc */
  async getGatewayEndpointDetails(
    agentId: string,
    agentService: string,
  ): Promise<AgentEndpointData> {
    const contentType = 'application/json';
    const proxyUrl = await this.discoveryApi.getBaseUrl('aiecosystem');
    const endpoint = isDatabricksAgent(agentService)
      ? 'dbx-endpoint'
      : 'gateway-endpoint';

    const response = await fetch(
      `${proxyUrl}${AgentGardenEndpoints.agents}/${agentId}/${endpoint}`,
      {
        method: 'GET',
        headers: {
          Accept: `${contentType}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Error - Statuscode ${response.status} - ${response.statusText} - Failed to fetch gateway endpoint details.`,
      );
    }

    return await response.json();
  }

  /** @inheritdoc */
  async deprecateAgent(
    agentId: string,
    formData: {
      agentName: string;
      deprecationDurationDays: number | null;
      deprecationReason: string;
      alternatives: string;
    },
  ): Promise<{
    agentId: string;
    agentName: string;
    status: string;
    deprecationStartedAt: string;
    deprecationCompletesAt: string;
    message: string;
  }> {
    const contentType = 'application/json';
    const proxyUrl = await this.discoveryApi.getBaseUrl('aiecosystem');

    const response = await fetch(
      `${proxyUrl}${AgentGardenEndpoints.agents}/${agentId}/deprecate`,
      {
        method: 'POST',
        headers: {
          Accept: `${contentType}`,
          'Content-Type': `${contentType}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      throw new Error(
        `Error - Statuscode ${response.status} - ${response.statusText} - Failed to deprecate agent.`,
      );
    }

    return await response.json();
  }

  // Dev-only simulation endpoint (404 in PROD, 403 for unauthorized users)
  async simulateRole(
    request: SimulateRoleRequest,
  ): Promise<SimulateRoleResponse> {
    const contentType = 'application/json';
    const proxyUrl = await this.discoveryApi.getBaseUrl('aiecosystem');

    const response = await fetch(`${proxyUrl}/dev/simulate-role`, {
      method: 'POST',
      headers: {
        Accept: `${contentType}`,
        'Content-Type': `${contentType}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      let errorMessage = `Failed to simulate role (${response.status} ${response.statusText})`;
      try {
        const errorData = await response.json();
        if (errorData.detail) {
          errorMessage = errorData.detail;
        }
      } catch {
        // Use default error message
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();

    const safeResponse: SimulateRoleResponse = {
      agentId: data.agentId,
      agentName: data.agentName ?? undefined,
      appliedRole: data.appliedRole ?? null,
      appliedAccessStatus: data.appliedAccessStatus ?? null,
      appliedAgentStatus: data.appliedAgentStatus ?? null,
      appliedDeprecation: data.appliedDeprecation ?? null,
      message: data.message ?? 'Simulation completed.',
      isDevModeAssignment: data.isDevModeAssignment ?? true,
      snapshotCreated: data.snapshotCreated ?? false,
    };

    return safeResponse;
  }

  /** @inheritdoc */
  async updateAgentOwnership(
    agentId: string,
    formData: {
      l3Manager: ContactData;
      aiProductOwner: ContactData;
      informationOwner: ContactData;
      technicalContacts: ContactData[];
    },
  ): Promise<{
    agentId: string;
    l3Manager: ContactData;
    aiProductOwner: ContactData;
    informationOwner: ContactData;
    technicalContacts: ContactData[];
    providerAccessReviewStatus: AgentAccessReviewStatus;
    agentAccessReviewStatus: AgentAccessReviewStatus;
    message: string;
  }> {
    const contentType = 'application/json';
    const proxyUrl = await this.discoveryApi.getBaseUrl('aiecosystem');

    const response = await fetch(
      `${proxyUrl}${AgentGardenEndpoints.agents}/${agentId}/${AgentGardenEndpoints.providerAccessReview}`,
      {
        method: 'PUT',
        headers: {
          Accept: `${contentType}`,
          'Content-Type': `${contentType}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      throw new Error(
        `Error - Statuscode ${response.status} - ${response.statusText} - Failed to update ownership assignments.`,
      );
    }

    return await response.json();
  }

  private async getAgentsPage(
    limit?: number,
    firstItemKey?: string,
  ): Promise<AgentListResponse> {
    const contentType = 'application/json';
    const proxyUrl = await this.discoveryApi.getBaseUrl('aiecosystem');

    const queryParams = new URLSearchParams();

    if (limit !== undefined) {
      queryParams.append('limit', limit.toString());
    }

    if (firstItemKey) {
      queryParams.append('first_item_key', firstItemKey);
    }

    const queryString = queryParams.toString();
    const agentsUrl = queryString
      ? `${proxyUrl}${AgentGardenEndpoints.agents}?${queryString}`
      : `${proxyUrl}${AgentGardenEndpoints.agents}`;

    const response = await fetch(agentsUrl, {
      method: 'GET',
      headers: {
        Accept: `${contentType}`,
      },
    });

    if (!response.ok) {
      throw new Error(
        `Error - Statuscode ${response.status} - ${response.statusText} - Failed to fetch agents.`,
      );
    }

    const data = (await response.json()) as AgentListResponse;

    return {
      agents: data.agents ?? [],
      lastItemKey: data.lastItemKey,
      canCreateUniversalAgents: data.canCreateUniversalAgents,
      isDevModeAuthorized: data.isDevModeAuthorized,
    };
  }
}
